import { Injectable } from '@angular/core';
import { AuthenticationService } from '../authentication.service';
import { DiskService } from '../disk/disk.service';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';
import { DeltaService, EntityNames } from '@omni/data-services/delta/delta.service';
import { OfflineServiceBase } from '../offline/offline.service';
import { DynamicsClientService } from '@omni/data-services/dynamics-client/dynamics-client.service';
import { DateTimeFormatsService } from '../date-time-formats/date-time-formats.service';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { AzureMapsService, PostalCodeResultType } from '../azure-maps/azure-maps.service';
import { BrickWithCoordinates } from '../contact-spotlight/rxsalesdata.types';
import { FeatureActionsMap } from '@omni/classes/authentication/user.class';
import { _ } from '@omni/utility/lodash-chain';

const POSTAL_CODE_RESULT_TYPES: PostalCodeResultType[] = ['postalCode', 'postalCode2'];

type RawBrick = {
  key: string;
  brickId: string;
  brickName: string;
  coordinates: [number, number][][][];
  stateCode: number;
  postalCodeStateCode: number;
  postalCodeId: string;
  postalCode: string;
  cityId: string;
  cityName: string;
  stateId: string;
  stateName: string;
  countryId: string;
  countryName: string;
  _deleted: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class BricksService extends OfflineServiceBase<RawBrick, 'key'> {
  readonly bricks$ = this.raw$
    .pipe(
      map(bricks => this.#groupPostalCodes(bricks)),
    );
  readonly bricksWithCoordinates$ = this.bricks$.pipe(
    switchMap(bricks => {
      return from(Promise.all(bricks.map(async brick => {
        let coordinates = brick.coordinates ?? [];
        let postalCodeBoundaries = (brick.postalCodes?.length
          ? await Promise.all(brick.postalCodes.map(postalCode => this.#getPostalCodeBoundary(postalCode.postalCode, postalCode.countryName)))
          : []).flat();
        return {
          ...brick,
          coordinates: [...coordinates, ...postalCodeBoundaries],
        } as BrickWithCoordinates;
      }))).pipe(catchError(error => this.#fallbackToConfiguredBoundaries(bricks, error)));
    }),
    shareReplay({ refCount: true, bufferSize: 1 })
  );
  constructor(
    disk: DiskService,
    deltaService: DeltaService,
    private readonly dynamics: DynamicsClientService,
    private readonly authService: AuthenticationService,
    private readonly azureMapsService: AzureMapsService,
    dateTimeFormatsService: DateTimeFormatsService,
  ) {
    super(disk, deltaService, EntityNames.brick, 'key', DB_KEY_PREFIXES.OMNIONE_BRICK, dateTimeFormatsService);
  }

  #groupPostalCodes(bricks: RawBrick[]) {
    return _(bricks)
      .groupBy('brickId')
      .map((bricks, brickId) => {
        const brickName = bricks[0].brickName;
        return {
          brickId,
          brickName,
          coordinates: bricks[0].coordinates,
          postalCodes: bricks.map(b => b.postalCode && ({ postalCode: b.postalCode, countryName: b.countryName })).filter(Boolean),
        }
      })
      .value();
  }


  *#getPolygons(geojson: any): Generator<[number, number][][]> {
    if (!geojson) return;

    // TODO: Holes not supported yet

    let array;

    switch (geojson.type) {
      case 'Polygon':
        // coordinates: [ [ [x,y], ... ] ]
        yield geojson.coordinates;
        break;

      case 'MultiPolygon':
        // coordinates: [ [ [ [x,y], ... ] ] ]
        yield* geojson.coordinates;
        break;

      case 'Feature':
        yield* this.#getPolygons(geojson.geometry);
        break;

      case 'FeatureCollection':
        array = geojson.features;
        break;

      case 'GeometryCollection':
        array = geojson.geometries;
        break;
    }
    if (!array) return;
    for (const item of array) {
      yield* this.#getPolygons(item);
    }
  }

  #parseBrickBoundaryFromGeoJSON(brickBoundary: string) {
    try {
      if (!brickBoundary) {
        return null;
      }
      const geoJson = JSON.parse(brickBoundary);
      return [...this.#getPolygons(geoJson)];
    } catch (error) {
      console.error('Error parsing brick boundary from GeoJSON', error);
      return null;
    }
  }

  protected override isSyncAllowed(): boolean | Promise<boolean> {
    return this.authService.hasFeatureAction(FeatureActionsMap.CONTACT_TERRITORY_PERFORMANCE);
  }

  protected async fetchData(lastUpdatedTime: string) {
    // Get user position IDs for filtering data based on user's access
    const positionIDs = this.authService.user.positions.map((o) => o.ID);

    // Build position condition for FetchXML query
    const positionCondition = positionIDs.length > 1 ? positionIDs.map(id => `<value>${id}</value>`).join('') : (positionIDs[0] ?? null);

    // Helper function to create position filter conditions for FetchXML
    const positionFilter = (entity, attribute) =>
      positionIDs.length > 1
        ? `<condition entityname="${entity}" attribute="${attribute}" operator="in">${positionCondition}</condition>`
        : `<condition entityname="${entity}" attribute="${attribute}" operator="eq" value="${positionCondition}" />`;


    const fetchXML = [
      '<fetch version="1.0" output-format="xml-platform" mapping="logical" no-lock="true" distinct="true">',
      '<entity name="omnione_brick">',
      '<attribute name="omnione_brickid" alias="brickId" />',
      '<attribute name="omnione_name" alias="brickName" />',
      '<attribute name="indskr_boundary" alias="brickBoundary" />',
      '<attribute name="statecode" alias="stateCode" />',
      '<filter>',
      '<filter type="or">',
      '<condition entityname="pc" attribute="indskr_lu_postalcodeid" operator="null"/>',
      '<condition entityname="country" attribute="indskr_lu_countryid" operator="not-null"/>',
      '</filter>',
      '<filter type="or">',
      ...lastUpdatedTime ? [
        `<condition attribute="modifiedon" operator="ge" value="${lastUpdatedTime}" />`,
        `<condition entityname="pc" attribute="modifiedon" operator="ge" value="${lastUpdatedTime}" />`,
        `<condition entityname="bp" attribute="modifiedon" operator="ge" value="${lastUpdatedTime}" />`,
      ] : [
        `<condition attribute="statecode" operator="eq" value="0" />`,
        `<condition entityname="bp" attribute="statecode" operator="eq" value="0" />`,
        `<condition entityname="pc" attribute="statecode" operator="eq" value="0" />`,
      ],
      '</filter>',
      '<filter type="or">',
      positionFilter('pos', 'positionid'),
      positionFilter('pos', 'parentpositionid'),
      '</filter>',
      '</filter>',
      '<link-entity name="omnione_brickposition" from="omnione_brick" to="omnione_brickid" alias="bp">',
      '<link-entity name="position" from="positionid" to="omnione_position" link-type="inner" alias="pos" />',
      '</link-entity>',
      '<link-entity name="omnione_omnione_brick_indskr_lu_postalcode" from="omnione_brickid" to="omnione_brickid" link-type="outer">',
      '<link-entity name="indskr_lu_postalcode" from="indskr_lu_postalcodeid" to="indskr_lu_postalcodeid" link-type="outer" alias="pc">',
      '<attribute name="indskr_lu_postalcodeid" alias="postalCodeId" />',
      '<attribute name="indskr_postalcode" alias="postalCode" />',
      '<attribute name="statecode" alias="postalCodeStateCode" />',
      '<link-entity name="indskr_lu_city" from="indskr_lu_cityid" to="indskr_city" link-type="outer" alias="city">',
      '<attribute name="indskr_lu_cityid" alias="cityId" />',
      '<attribute name="indskr_name" alias="cityName" />',
      '<link-entity name="indskr_lu_state" from="indskr_lu_stateid" to="indskr_state" link-type="outer" alias="state">',
      '<attribute name="indskr_lu_stateid" alias="stateId" />',
      '<attribute name="indskr_state" alias="stateName" />',
      '<link-entity name="indskr_lu_country" from="indskr_lu_countryid" to="indskr_lu_countryid" link-type="outer" alias="country">',
      '<attribute name="indskr_lu_countryid" alias="countryId" />',
      '<attribute name="indskr_countryname" alias="countryName" />',
      '</link-entity>',
      '</link-entity>',
      '</link-entity>',
      '</link-entity>',
      '</link-entity>',
      '</entity>',
      '</fetch>'
    ].join('');
    const response = await this.dynamics.executeFetchQuery<RawBrick>('omnione_bricks', fetchXML);
    return response.map(o => {
      if ((o as any).brickBoundary) {
        o.coordinates = this.#parseBrickBoundaryFromGeoJSON((o as any).brickBoundary);
        delete (o as any).brickBoundary;
      } else {
        o.coordinates = undefined;
      }
      const key = [o.brickId, o.postalCodeId].filter(Boolean).join('.');
      o.key = key;
      if (o.stateCode || o.postalCodeStateCode) {
        o._deleted = true;
      }
      return o;
    });
  }

  /**
   * Emits the bricks carrying only their configured `indskr_boundary` geometry.
   * Used when postal-code boundary resolution fails, so an unavailable lookup
   * degrades the territory rather than terminating the stream.
   */
  #fallbackToConfiguredBoundaries(bricks: { coordinates?: [number, number][][][] }[], error: unknown) {
    console.error('Brick boundary resolution failed; falling back to configured boundaries:', error);
    const configured = bricks.map(brick => ({ ...brick, coordinates: brick.coordinates ?? [] }));
    return of(configured as BrickWithCoordinates[]);
  }

  async #readBrickBoundaryFromDisk(name: string) {
    const result = await this.disk.retrieve(`${DB_KEY_PREFIXES.OMNIONE_BRICK_BOUNDARY}_${name}`);
    return result?.coordinates as [number, number][][][] ?? null;
  }

  async #fetchBoundary(postalCode: string, country: string) {
    const coordinates = await this.azureMapsService.geocode(postalCode, country);
    for (const resultType of POSTAL_CODE_RESULT_TYPES) {
      const polygons = await this.#searchPostalCodeBoundary(coordinates, resultType, postalCode);
      if (polygons.length) {
        return polygons;
      }
    }
    console.warn(`Azure Maps holds no boundary for postal code ${country}.${postalCode}; brick renders without it.`);
    return [];
  }

  /**
   * Retrieves the boundary for `postalCode` at one result type.
   *
   * Yields no geometry when Azure Maps holds no boundary of that result type,
   * and when the boundary it resolves belongs to a different postal code —
   * boundaries accumulate on the brick, so an adjacent area's geometry would
   * widen the territory beyond what is assigned.
   */
  async #searchPostalCodeBoundary(coordinates: [number, number], resultType: PostalCodeResultType, postalCode: string) {
    let result: { name: string | null, polygons: [number, number][][][] };
    try {
      result = await this.azureMapsService.searchPolygon(coordinates, resultType);
    } catch {
      return [];
    }
    if (result.name && result.name !== postalCode) {
      console.warn(`Azure Maps resolved postal code ${postalCode} to ${result.name} at ${resultType}; boundary discarded.`);
      return [];
    }
    return result.polygons;
  }

  async #getPostalCodeBoundary(postalCode: string, country: string) {
    const key = `${country}.${postalCode}`;
    const result = await this.#readBrickBoundaryFromDisk(key);
    if (result) {
      return result;
    }
    try {
      const coordinates = await this.#fetchBoundary(postalCode, country);
      if (!coordinates.length) {
        return coordinates;
      }
      await this.disk.updateOrInsert(`${DB_KEY_PREFIXES.OMNIONE_BRICK_BOUNDARY}_${key}`, (doc) => ({
        ...doc ?? {},
        coordinates: coordinates,
      }));
      return coordinates;
    } catch (error) {
      console.warn(`Brick boundary unavailable for postal code ${key}; brick renders without it:`, error);
      return [];
    }
  }
}
